import React from 'react';
import { Button, Space, Table, Tooltip, Typography } from 'antd';

import StatusTag from '../../../../components/StatusTag.jsx';
import { formatMoney } from '../../../../format';
import LineNotes from './LineNotes.jsx';
import StatementMatchCell from './StatementMatchCell.jsx';
import { lineActions, ownClaimNote, signedAmount, statusTags } from './statementLines.js';

const { Text } = Typography;

/**
 * The statement's lines (design 8, "Columns"): Reference, the signed amount,
 * Value date, Description, Transaction ID (the matched ref, or the candidate
 * picker on an open credit), Status, Action and Comment. What each line
 * offers is statementLines.js lineActions'; the screen (and only it) holds
 * the gates and the step-up dialog, and passes them in.
 *
 * @param {{ lines: object[], loading?: boolean, me?: string|null, gates: Record<string, boolean>, api: object,
 *           withStepUp: Function, onAction: (key: string, line: object) => void, onClaimed: () => void,
 *           onStale: () => void, onNoted: (line: object, note: string) => void }} props
 */
export const StatementLinesTable = ({ lines, loading = false, me = null, gates, api, withStepUp, onAction, onClaimed, onStale, onNoted }) => {
    const columns = [
        { title: 'Reference', dataIndex: 'referenceId', key: 'referenceId', render: (v) => (v ? <span>{v}</span> : <Text type="secondary">—</Text>) },
        {
            title: 'Amount (LKR)',
            key: 'amount',
            align: 'right',
            width: 140,
            render: (_, line) => <span className={line.direction === 'DBIT' ? 'statement-debit' : undefined}>{formatMoney(signedAmount(line))}</span>
        },
        { title: 'Value date', dataIndex: 'valueDate', key: 'valueDate', width: 110 },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            className: 'statement-description-cell',
            render: (v) => v || <Text type="secondary">—</Text>
        },
        {
            title: 'Transaction ID',
            key: 'transactionRef',
            width: 300,
            render: (_, line) => (
                <StatementMatchCell line={line} canMatch={gates.match} api={api} withStepUp={withStepUp} onClaimed={onClaimed} onStale={onStale} />
            )
        },
        {
            title: 'Status',
            key: 'status',
            width: 220,
            render: (_, line) => (
                <Space size={[0, 4]} wrap>
                    {/* Each tag explains itself on hover or focus (statusTags' title). */}
                    {statusTags(line).map((t) => <StatusTag key={t.label} color={t.colour} label={t.label} help={t.title} />)}
                </Space>
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 200,
            render: (_, line) => {
                const offered = lineActions(line, { me, gates });
                const own = ownClaimNote(line, me);
                if (!offered.length) return own ? <Text type="secondary" className="statement-own-claim">{own}</Text> : <Text type="secondary">—</Text>;
                return (
                    <Space size={4} wrap>
                        {offered.map((a) => {
                            const button = (
                                <Button
                                    key={a.key}
                                    className={`statement-action-button${a.outlined ? ' statement-action-button-outlined' : ''}`}
                                    size="small"
                                    type={a.danger || a.outlined ? 'default' : 'primary'}
                                    danger={a.danger}
                                    disabled={Boolean(a.why)}
                                    onClick={() => onAction(a.key, line)}
                                >
                                    {a.label}
                                </Button>
                            );
                            return a.why ? <Tooltip key={a.key} title={a.why}>{button}</Tooltip> : button;
                        })}
                    </Space>
                );
            }
        },
        {
            title: 'Comment',
            key: 'comment',
            width: 240,
            render: (_, line) => <LineNotes line={line} canNote={gates.note} api={api} onAdded={(note) => onNoted(line, note)} />
        }
    ];

    return (
        <Table
            className="statement-lines-table"
            rowKey="lineId"
            size="small"
            loading={loading}
            columns={columns}
            dataSource={lines}
            pagination={false}
            locale={{ emptyText: 'No statement lines' }}
            scroll={{ x: 'max-content' }}
        />
    );
};

export default StatementLinesTable;
